/**
 * =============================================================================
 * Componente: PayoutStatusBadge
 * Versão: 1.0.0
 * Release: 2026-05-02 — Util compartilhado para status de PAGAMENTO (payout)
 *
 * Descrição:
 *   Padroniza a exibição do status de um saque/pagamento ao afiliado
 *   (pending / processing / paid / failed / cancelled) em qualquer tela.
 *
 *   Visual oficial: "dot colorido + texto" — mesmo padrão de
 *   `ReferralStatusBadge`.
 *
 *   Também exporta `PAYOUT_STATUS_CONFIG` para reuso da cor em barras
 *   laterais e elementos auxiliares.
 * =============================================================================
 */
import { cn } from '@/lib/utils';

export type PayoutStatus =
  | 'pending'
  | 'processing'
  | 'paid'
  | 'failed'
  | 'cancelled';

export const PAYOUT_STATUS_CONFIG: Record<
  PayoutStatus,
  { label: string; dot: string; bar: string }
> = {
  // Âmbar: solicitado, aguardando processamento
  pending:    { label: 'Pendente',    dot: 'bg-amber-500',           bar: 'bg-amber-500' },
  // Azul: em processamento
  processing: { label: 'Processando', dot: 'bg-blue-500',            bar: 'bg-blue-500' },
  // Verde: pago
  paid:       { label: 'Pago',        dot: 'bg-green-500',           bar: 'bg-green-500' },
  // Vermelho: falha no processamento
  failed:     { label: 'Falhou',      dot: 'bg-red-500',             bar: 'bg-red-500' },
  // Cinza: cancelado
  cancelled:  { label: 'Cancelado',   dot: 'bg-muted-foreground/40', bar: 'bg-muted-foreground/40' },
};

interface PayoutStatusBadgeProps {
  status: string | null | undefined;
  className?: string;
  fallback?: React.ReactNode;
}

export function PayoutStatusBadge({
  status,
  className,
  fallback,
}: PayoutStatusBadgeProps) {
  const cfg = status
    ? PAYOUT_STATUS_CONFIG[status as PayoutStatus]
    : null;

  if (!cfg) {
    return (
      <>{fallback ?? <span className="text-xs text-muted-foreground">—</span>}</>
    );
  }

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 text-xs font-medium text-foreground whitespace-nowrap',
        className,
      )}
    >
      <span aria-hidden className={cn('h-2 w-2 rounded-full shrink-0', cfg.dot)} />
      {cfg.label}
    </span>
  );
}
